import {
  Box,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { Link as ReactRouterLink } from "react-router-dom";

import SectionHeading from "./SectionHeading";
import BetaPill from "../../components/BetaPill";
import { StyledLink } from "../../components/CustomMUI";
import { EXPLORE_SECTIONS } from "../explore/exploreSections";

// Built from the Explore registry so the buttons stay in step as sections are
// added or renamed — they're also the homepage's crawlable links into each
// section page.
const COLLECTION_SECTIONS = EXPLORE_SECTIONS.filter(
  (section) => section.type === "collection",
);

/** Homepage pitch for Explore, with a button into each collection section. */
export default function ExploreSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // A 2x2 grid under the heading (small buttons on mobile). A max-content grid
  // with 1fr columns sizes both columns to the widest label, so every button is
  // the same width and no label wraps. Divider-coloured outline and secondary
  // text rather than primary, so the four buttons don't overpower the section.
  const sectionButtons = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 1,
        width: "max-content",
        mx: "auto",
      }}
    >
      {COLLECTION_SECTIONS.map((section) => (
        <Button
          key={section.slug}
          variant="outlined"
          color="inherit"
          size={isMobile ? "small" : "medium"}
          component={ReactRouterLink}
          to={`/explore/${section.slug}`}
          sx={{
            whiteSpace: "nowrap",
            px: 1.5,
            borderColor: "divider",
            color: "text.secondary",
          }}
        >
          {section.title}
        </Button>
      ))}
    </Box>
  );

  return (
    <Grid container spacing={0} sx={{ maxWidth: "md" }}>
      <Grid
        item
        xs={12}
        sm={6}
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <SectionHeading
          icon="explore"
          title={
            <>
              <b>Explore</b> <BetaPill />
            </>
          }
          justifyContent={{ xs: "center" }}
        />

        <Box sx={{ mt: 2, mb: { xs: 4, sm: 0 }, width: "100%" }}>
          {sectionButtons}
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box maxWidth={{ xs: "250px", sm: "250px", md: "500px" }}>
          <Typography sx={{ mb: 1.5 }}>
            <strong>Stay in the loop.</strong> Follow friends to see what
            they're watching, reading, and curating — no algorithm deciding for
            you.
          </Typography>
          <Typography sx={{ mb: 1.5 }}>
            <strong>Only the best, hand-picked.</strong> Browse{" "}
            <StyledLink
              component={ReactRouterLink}
              to="/explore/recommended-by"
            >
              the favourite books and films of famous authors and directors
            </StyledLink>
            ,{" "}
            <StyledLink
              component={ReactRouterLink}
              to="/explore/critics-favourites"
            >
              critics' best-of-the-year lists
            </StyledLink>
            ,{" "}
            <StyledLink component={ReactRouterLink} to="/explore/award-winners">
              award winners from the Oscars to the Booker Prize
            </StyledLink>
            , and{" "}
            <StyledLink
              component={ReactRouterLink}
              to="/explore/essential-reference"
            >
              the all-time greatest films, books and TV shows
            </StyledLink>
            .
          </Typography>
          <Typography sx={{ mb: 1.5 }}>
            <strong>The vision: recommendations you trust.</strong> As the
            community grows, discover people whose taste matches yours and get
            recommendations you can actually trust.
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
